class RavemontUI {
  constructor() {
    this.header = document.querySelector('[data-rv-header]');
    this.bindHeader();
    this.bindCatalogs();
    this.bindGalleries();
    this.bindVariants();
    this.bindStickyButtons();
  }

  bindHeader() {
    if (!this.header) return;
    const triggers = [...this.header.querySelectorAll('.rv-nav-trigger')];
    let closeTimer;

    const close = (trigger, returnFocus = false) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      if (returnFocus) trigger.focus();
    };

    const closeAll = (except) => triggers.forEach((trigger) => {
      if (trigger !== except) close(trigger);
    });

    const open = (trigger, clicked = false) => {
      window.clearTimeout(closeTimer);
      closeAll(trigger);
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      trigger.setAttribute('aria-expanded', 'true');
      if (clicked) trigger.dataset.rvClickOpen = 'true';
      if (panel) panel.hidden = false;
    };

    triggers.forEach((trigger) => {
      const item = trigger.closest('.rv-nav-item');
      trigger.addEventListener('click', () => {
        if (trigger.getAttribute('aria-expanded') === 'true' && trigger.dataset.rvClickOpen === 'true') {
          delete trigger.dataset.rvClickOpen;
          close(trigger);
        } else {
          open(trigger, true);
        }
      });
      item.addEventListener('mouseenter', () => open(trigger));
      item.addEventListener('mouseleave', () => {
        closeTimer = window.setTimeout(() => {
          delete trigger.dataset.rvClickOpen;
          close(trigger);
        }, 220);
      });
      item.addEventListener('focusin', () => open(trigger));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) close(trigger);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); close(trigger, true); }
      });
    });

    document.addEventListener('click', (event) => {
      if (!this.header.contains(event.target)) closeAll();
    });

    this.header.querySelectorAll('[data-rv-close-mobile]').forEach((button) => {
      button.addEventListener('click', () => {
        const details = button.closest('.rv-mobile-menu');
        if (details) details.open = false;
      });
    });
  }

  bindCatalogs() {
    document.querySelectorAll('[data-rv-catalog]').forEach((catalog) => {
      const key = 'ravemont-catalog-view';
      let view = sessionStorage.getItem(key) || 'small';
      const buttons = [...catalog.querySelectorAll('[data-rv-view]')];

      const apply = (next) => {
        if (!['list', 'small', 'large'].includes(next)) next = 'small';
        view = next;
        catalog.dataset.view = view;
        sessionStorage.setItem(key, view);
        buttons.forEach((button) => {
          const active = button.dataset.rvView === view;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      };

      buttons.forEach((button) => button.addEventListener('click', () => apply(button.dataset.rvView)));
      apply(view);
    });
  }

  bindGalleries() {
    document.querySelectorAll('[data-rv-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-rv-main-image]');
      gallery.querySelectorAll('[data-rv-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!main) return;
          main.src = thumb.dataset.image;
          main.srcset = '';
          main.alt = thumb.dataset.alt || '';
          gallery.querySelectorAll('[data-rv-thumb]').forEach((item) => item.setAttribute('aria-current', 'false'));
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  bindVariants() {
    document.querySelectorAll('[data-rv-product]').forEach((root) => {
      const select = root.querySelector('[data-rv-variant]');
      const json = root.querySelector('[data-rv-variants]');
      if (!select || !json) return;
      let variants;
      try { variants = JSON.parse(json.textContent); } catch (_) { return; }
      const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

      select.addEventListener('change', () => {
        const variant = variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        const price = root.querySelector('[data-rv-price]');
        const compare = root.querySelector('[data-rv-compare]');
        const stock = root.querySelector('[data-rv-availability]');
        const sku = root.querySelector('[data-rv-sku]');
        const add = root.querySelector('[data-rv-add]');
        const sticky = root.querySelector('[data-rv-sticky-submit]');
        if (price) price.textContent = money.format(Number(variant.price) / 100);
        if (compare) {
          const hasCompare = Number(variant.compare_at_price || 0) > Number(variant.price);
          compare.hidden = !hasCompare;
          compare.textContent = hasCompare ? money.format(Number(variant.compare_at_price) / 100) : '';
        }
        if (stock) {
          stock.classList.toggle('is-out', !variant.available);
          stock.lastChild.textContent = variant.available ? 'In stock' : 'Sold out';
        }
        if (sku) sku.textContent = variant.sku || '';
        if (add) {
          add.disabled = !variant.available;
          add.textContent = variant.available ? 'ADD TO BAG' : 'SOLD OUT';
        }
        if (sticky) sticky.disabled = !variant.available;
        if (variant.featured_image?.src) {
          const main = root.querySelector('[data-rv-main-image]');
          if (main) { main.src = variant.featured_image.src; main.srcset = ''; }
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      });
    });
  }

  bindStickyButtons() {
    document.querySelectorAll('[data-rv-sticky-submit]').forEach((button) => {
      button.addEventListener('click', () => {
        const form = document.getElementById(button.dataset.form);
        if (form) form.requestSubmit();
      });
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new RavemontUI(), { once: true });
} else {
  new RavemontUI();
}
